import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calculator, Info } from "@phosphor-icons/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Slider } from "../components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";

// Pricing configuration
const PRICING = {
  fullDay: {
    base: 65, // Base price per full day
    discount: {
      2: 0.05,  // 5% off for 2 days
      3: 0.10,  // 10% off for 3 days
      4: 0.15,  // 15% off for 4 days
      5: 0.20,  // 20% off for 5 days
      6: 0.25,  // 25% off for 6 days
    }
  },
  halfDay: {
    base: 40, // Base price per half day
    discount: {
      2: 0.05,
      3: 0.08,
      4: 0.12,
      5: 0.15,
      6: 0.18,
    }
  }
};

const WEEKS_PER_MONTH = 4.33;

const PriceCalculator = () => {
  const [bookingType, setBookingType] = useState("fullDay");
  const [daysPerWeek, setDaysPerWeek] = useState([2]);
  const [prices, setPrices] = useState({
    monthlyNet: 0,
    monthlyGross: 0,
    singleBookingPrice: 0,
    monthlySavings: 0,
    avgDailyPrice: 0,
  });

  useEffect(() => {
    calculatePrices();
  }, [bookingType, daysPerWeek]);

  const calculatePrices = () => {
    const days = daysPerWeek[0];
    const pricing = bookingType === "fullDay" ? PRICING.fullDay : PRICING.halfDay;
    const basePrice = pricing.base;
    
    // Get discount percentage based on days
    const discountPercent = pricing.discount[days] || 0;
    
    // Calculate discounted price per day
    const discountedDayPrice = basePrice * (1 - discountPercent);
    
    // Monthly calculations
    const daysPerMonth = days * WEEKS_PER_MONTH;
    const monthlyNet = discountedDayPrice * daysPerMonth;
    const monthlyGross = monthlyNet * 1.19; // 19% MwSt
    
    // Single booking price (no discount)
    const singleBookingPrice = basePrice * daysPerMonth;
    const singleBookingGross = singleBookingPrice * 1.19;
    
    // Savings
    const monthlySavings = singleBookingGross - monthlyGross;
    
    // Average daily price
    const avgDailyPrice = monthlyGross / daysPerMonth;

    setPrices({
      monthlyNet: Math.round(monthlyNet),
      monthlyGross: Math.round(monthlyGross),
      singleBookingPrice: Math.round(singleBookingGross),
      monthlySavings: Math.round(monthlySavings),
      avgDailyPrice: Math.round(avgDailyPrice * 100) / 100,
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR',
    }).format(value);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8 }}
      className="bg-white border border-[#E6E2D6] p-8 lg:p-10"
      data-testid="price-calculator"
    >
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-full bg-[#D4AF37]/10 flex items-center justify-center">
          <Calculator size={24} className="text-[#D4AF37]" weight="light" />
        </div>
        <div>
          <h3
            className="text-2xl font-medium"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Dein Platz, dein Preis
          </h3>
          <p className="text-sm text-[#2C2C2C]/60">Berechne deine monatlichen Kosten</p>
        </div>
      </div>

      <div className="space-y-8">
        {/* Booking Type */}
        <div>
          <label className="block text-sm font-medium mb-3">
            Buchungsart
          </label>
          <Select value={bookingType} onValueChange={setBookingType}>
            <SelectTrigger 
              className="w-full bg-[#F9F7F2] border-[#E6E2D6] focus:border-[#9A8C73]"
              data-testid="booking-type-select"
            >
              <SelectValue placeholder="Buchungsart wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fullDay">Ganzer Tag</SelectItem>
              <SelectItem value="halfDay">Halber Tag</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Days per Week */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="text-sm font-medium">
              Arbeitstage pro Woche
            </label>
            <span className="text-lg font-semibold text-[#9A8C73]">
              {daysPerWeek[0]} {daysPerWeek[0] === 1 ? 'Tag' : 'Tage'}
            </span>
          </div>
          <Slider
            value={daysPerWeek}
            onValueChange={setDaysPerWeek}
            min={1}
            max={6}
            step={1}
            className="py-4"
            data-testid="days-slider"
          />
          <div className="flex justify-between text-xs text-[#2C2C2C]/40 mt-1">
            <span>1 Tag</span>
            <span>6 Tage</span>
          </div>
        </div>

        {/* Discount Info */}
        {daysPerWeek[0] >= 2 && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="bg-[#D4AF37]/10 p-4 rounded-sm"
          >
            <p className="text-sm text-[#9A8C73] font-medium">
              🎉 {Math.round((bookingType === "fullDay" ? PRICING.fullDay.discount[daysPerWeek[0]] : PRICING.halfDay.discount[daysPerWeek[0]]) * 100)}% Rabatt für {daysPerWeek[0]} Tage pro Woche!
            </p>
          </motion.div>
        )}

        {/* Divider */}
        <div className="border-t border-[#E6E2D6]" />

        {/* Results */}
        <div className="space-y-4">
          {/* Main Price */}
          <div className="bg-[#F9F7F2] p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-[#2C2C2C]/60">JS Vorteilspreis netto:</span>
              <span className="text-lg font-semibold" data-testid="price-net">
                {formatCurrency(prices.monthlyNet)}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">inkl. MwSt.:</span>
              <span className="text-2xl font-bold text-[#9A8C73]" data-testid="price-gross">
                {formatCurrency(prices.monthlyGross)}
              </span>
            </div>
            <p className="text-xs text-[#2C2C2C]/40 mt-2">pro Monat</p>
          </div>

          {/* Additional Info */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="p-4 bg-[#F9F7F2]">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex items-center gap-1 cursor-help">
                      <span className="text-xs text-[#2C2C2C]/60">Preis bei Einzelbuchungen:</span>
                      <Info size={14} className="text-[#2C2C2C]/40" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Ohne Mengenrabatt</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <p className="text-lg font-semibold mt-1 line-through text-[#2C2C2C]/40" data-testid="single-price">
                {formatCurrency(prices.singleBookingPrice)}
              </p>
            </div>

            <div className="p-4 bg-[#D4AF37]/10">
              <span className="text-xs text-[#9A8C73]">Monatliche Ersparnis:</span>
              <p className="text-lg font-semibold mt-1 text-[#9A8C73]" data-testid="savings">
                {formatCurrency(prices.monthlySavings)}
              </p>
            </div>
          </div>

          <div className="p-4 bg-[#F9F7F2]">
            <span className="text-xs text-[#2C2C2C]/60">Durchschnittlicher Tagespreis:</span>
            <p className="text-lg font-semibold mt-1" data-testid="daily-price">
              {formatCurrency(prices.avgDailyPrice)}
            </p>
          </div>
        </div>

        {/* CTA */}
        <motion.a
          href="mailto:info@js-blondsalon.de?subject=Co-Working Anfrage"
          data-testid="calculator-cta-btn"
          className="btn-primary w-full text-center block"
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          Jetzt Anfragen
        </motion.a>

        <p className="text-xs text-center text-[#2C2C2C]/40">
          * Alle Preise verstehen sich zzgl. einmaliger Kaution. 
          Kabinettware und Salonfee-Organisation inklusive.
        </p>
      </div>
    </motion.div>
  );
};

export default PriceCalculator;
